import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import { signInWithEmailAndPassword, AuthError } from 'firebase/auth';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { getFirebaseAuth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getAuthErrorMessage } from '../utils/authErrors';

/**
 * Login page component
 *
 * Provides email/password authentication with MFA support
 * Integrates with Firebase Authentication
 */
export default function Login(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get the page user was trying to access, or default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);

      console.log('[Login] Successfully authenticated');

      // Navigation is handled by useEffect hook above
      // which monitors the user state from AuthContext
    } catch (err) {
      console.error('[Login] Authentication failed:', err);

      if ((err as AuthError).code) {
        setError(getAuthErrorMessage(err as AuthError));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card elevation={0} sx={{ borderRadius: '4px' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/sophia-logo.svg"
                alt="Sophia Learning"
                sx={{ height: 60, mr: 2 }}
              />
              <div>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Sophia Learning Partner Portal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign in to access student data
                </Typography>
              </div>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
              />

              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 1 }}
                disabled={loading}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  state={email ? { email } : undefined}
                  variant="body2"
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Compliance Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            This system processes student data in compliance with FERPA, COPPA, and SOC 2 Type II
            standards.
            <br />
            Unauthorized access is prohibited.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
